/** @jsxImportSource @emotion/react */

import { DateTime } from 'luxon';

export type DateFilterValue =
  | {
      option: 'is_in_the_last';
      unit: 'days' | 'months';
      period: string;
    }
  | {
      option: 'is_between';
      dates: string[];
    }
  | {
      option: 'is_equal_to' | 'is_after' | 'is_before';
      date: string;
    };

export const defaultDateFilter: DateFilterValue = {
  option: 'is_in_the_last',
  unit: 'days',
  period: '',
};

export type DateFilterQueryInput = {
  gte?: string | null;
  lte?: string | null;
  gt?: string | null;
  lt?: string | null;
  eq?: string | null;
};

export const toInstantFilterInput = (dateFilter: DateFilterValue): DateFilterQueryInput | null => {
  const { option } = dateFilter;

  if (option === 'is_in_the_last' && 'unit' in dateFilter) {
    const now = DateTime.now();

    return {
      gte: now
        .minus({ [dateFilter.unit]: parseInt(dateFilter.period) })
        .startOf('day')
        .toUTC()
        .toISO(),
    };
  } else if (option === 'is_between' && 'dates' in dateFilter) {
    const start = DateTime.fromISO(dateFilter.dates[0]);
    const end = DateTime.fromISO(dateFilter.dates[1]);

    return { gte: start.startOf('day').toUTC().toISO(), lte: end.endOf('day').toUTC().toISO() };
  } else if ('date' in dateFilter) {
    if (option === 'is_equal_to') {
      const date = DateTime.fromISO(dateFilter.date);

      return { gte: date.startOf('day').toUTC().toISO(), lte: date.endOf('day').toUTC().toISO() };
    } else if (option === 'is_after') {
      const date = DateTime.fromISO(dateFilter.date);

      return { gt: date.endOf('day').toUTC().toISO() };
    } else if (option === 'is_before') {
      const date = DateTime.fromISO(dateFilter.date);

      return { lt: date.startOf('day').toUTC().toISO() };
    }
  }

  return null;
};

export const toLocalDateFilterInput = (dateFilter: DateFilterValue): DateFilterQueryInput | null => {
  const { option } = dateFilter;

  if (option === 'is_in_the_last' && 'unit' in dateFilter) {
    const now = DateTime.now();

    return {
      gte: now
        .minus({ [dateFilter.unit]: parseInt(dateFilter.period) })
        .startOf('day')
        .toISODate(),
    };
  } else if (option === 'is_between' && 'dates' in dateFilter) {
    const start = DateTime.fromISO(dateFilter.dates[0]);
    const end = DateTime.fromISO(dateFilter.dates[1]);

    return { gte: start.startOf('day').toISODate(), lte: end.endOf('day').toISODate() };
  } else if ('date' in dateFilter) {
    if (option === 'is_equal_to') {
      const date = DateTime.fromISO(dateFilter.date);

      return { eq: date.startOf('day').toISODate() };
    } else if (option === 'is_after') {
      const date = DateTime.fromISO(dateFilter.date);

      return { gt: date.endOf('day').toISODate() };
    } else if (option === 'is_before') {
      const date = DateTime.fromISO(dateFilter.date);

      return { lt: date.startOf('day').toISODate() };
    }
  }

  return null;
};

export type DateFilterProps = {
  value: DateFilterValue | null;
  conditions?: string[];
  onChange(value: DateFilterValue): void;
};

export function DateFilter({ onChange, ...props }: DateFilterProps) {
  const value = props.value ?? defaultDateFilter;
  const conditions = props.conditions;

  return (
    <>
      <div className="lp-w-full mb-16">
        <div className="selectdropdown">
          <select
            data-testid="date-filter-type-select"
            className="form-control"
            value={value.option}
            onChange={({ target: { value: option } }) =>
              onChange(
                option === 'is_in_the_last'
                  ? { option, unit: 'days', period: '' }
                  : option === 'is_between'
                  ? { option, dates: ['', ''] }
                  : ({ option, date: '' } as DateFilterValue),
              )
            }
          >
            {!conditions || conditions.includes('is_in_the_last') ? (
              <option value="is_in_the_last">is in the last</option>
            ) : (
              ''
            )}
            {!conditions || conditions.includes('is_equal_to') ? <option value="is_equal_to">is equal to</option> : ''}
            {!conditions || conditions.includes('is_between') ? <option value="is_between">is between</option> : ''}
            {!conditions || conditions.includes('is_after') ? <option value="is_after">is after</option> : ''}
            {!conditions || conditions.includes('is_before') ? <option value="is_before">is before</option> : ''}
          </select>
        </div>
      </div>
      <div className="lp-w-full lp-flex lp-align-center mb-0">
        {value.option === 'is_in_the_last' ? (
          <>
            <div className="w-40">
              <input
                type="text"
                className="form-control"
                value={value.period}
                onChange={({ target: { value: period } }) => onChange({ ...value, period })}
                data-testid="date-filter-number"
              />
            </div>
            <div className="selectdropdown">
              <select
                className="form-control"
                value={value.unit}
                onChange={({ target: { value: unit } }) => onChange({ ...value, unit } as DateFilterValue)}
                data-testid="date-filter-periods-select"
              >
                <option value="days">days</option>
                <option value="months">months</option>
              </select>
            </div>
          </>
        ) : value.option === 'is_between' ? (
          <div className="lp-w-full ml-16px">
            <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <input
                type="date"
                className="form-control"
                value={value.dates[0]}
                onChange={({ target: { value: date } }) => onChange({ ...value, dates: [date, value.dates[1]] })}
                data-testid="date-filter-startdate-input"
              />
              <span>-</span>
              <input
                type="date"
                className="form-control"
                value={value.dates[1]}
                onChange={({ target: { value: date } }) => onChange({ ...value, dates: [value.dates[0], date] })}
                data-testid="date-filter-enddate-input"
              />
            </div>
          </div>
        ) : (
          <div className="lp-w-full ml-16px">
            <input
              type="date"
              className="form-control"
              value={value.date}
              onChange={({ target: { value: date } }) => onChange({ ...value, date })}
              data-testid="date-filter-date-input"
            />
          </div>
        )}
      </div>
    </>
  );
}
